import React, { useState } from "react";
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  useTheme,
  CircularProgress,
  Snackbar,
  Stack,
  Tooltip,
} from "@mui/material";
import MuiAlert from "@mui/material/Alert";
import ky from "ky";

import { slackWebhookUrl } from "../config";
import {
  MARKETING_EVENTS,
  submitLead,
  trackMarketingEvent,
} from "../utils/demoTracking";

/**
 * The three fields we actually need, each with the message shown when it is wrong.
 *
 * The form previously leaned entirely on the `required` attribute, which hands the job to the
 * browser's own validation bubble: it disappears on the next keystroke, is not reliably surfaced by
 * screen readers, and says "Please fill out this field" regardless of what the field is for. Owning
 * the messages lets each one say what to do about it, and lets it stay on screen and in the
 * accessibility tree until it is fixed.
 */
const REQUIRED_FIELDS = {
  user_name: {
    id: "contact-name",
    validate: (value) =>
      value.trim() ? "" : "Enter your name so we know who we are replying to.",
  },
  user_email: {
    id: "contact-email",
    validate: (value) => {
      if (!value.trim()) {
        return "Enter your email address so we have somewhere to reply.";
      }
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim())
        ? ""
        : "Enter an email address in the form name@example.com.";
    },
  },
  message: {
    id: "contact-message",
    validate: (value) =>
      value.trim() ? "" : "Tell us what you would like help with.",
  },
};

const ContactForm = () => {
  const theme = useTheme();
  const [contactDetails, setContactDetails] = useState({
    user_name: "",
    user_email: "",
    unit_type: "",
    unit_number: "",
    expected_event_date: "",
    message: "",
    // Honeypot. Hidden from people, irresistible to the form-spam bots that fill in every input they
    // find; a submission with this set is dropped server-side.
    website: "",
  });
  const [isLoading, setIsLoading] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const setFieldError = (name, message) => {
    setFieldErrors((previous) => {
      const next = { ...previous };
      if (message) {
        next[name] = message;
      } else {
        delete next[name];
      }
      return next;
    });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setContactDetails({ ...contactDetails, [name]: value });
    // Only ever clears an error, never raises one: flagging a field as invalid while somebody is
    // still part-way through typing into it is the interruption everyone hates.
    if (fieldErrors[name] && !REQUIRED_FIELDS[name]?.validate(value)) {
      setFieldError(name, "");
    }
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    setFieldError(name, REQUIRED_FIELDS[name].validate(value));
  };

  /**
   * Props that tie a field to its own error message.
   *
   * `aria-invalid` is what tells a screen reader the field is in an error state at all, and
   * `aria-describedby` is what makes it read the reason out when focus lands there, rather than the
   * reason existing only as red text a sighted user can see.
   */
  const validationProps = (name) => {
    const { id } = REQUIRED_FIELDS[name];
    const message = fieldErrors[name];
    return {
      id,
      error: Boolean(message),
      helperText: message,
      onBlur: handleBlur,
      inputProps: {
        "aria-invalid": Boolean(message),
        "aria-describedby": message ? `${id}-helper-text` : undefined,
      },
    };
  };

  /**
   * Posts the enquiry to the API first, and only falls back to the browser-to-Slack webhook if that
   * fails.
   *
   * The webhook used to be the only destination, which meant a lead lived in exactly one place with no
   * record anywhere if the request did not land. The API path gives it a database row, the honeypot,
   * a server-side rate limit and the lead counter; the webhook survives as a fallback purely so a
   * prospect's message still reaches somebody on the day the API is the thing that is down.
   */
  const sendToSlackWebhook = async () => {
    if (!slackWebhookUrl) {
      throw new Error("No Slack webhook configured");
    }
    const { user_name, user_email, unit_type, unit_number, message } =
      contactDetails;
    const unit = [unit_type, unit_number].filter(Boolean).join(" ");
    // `json:` already sets application/json. The previous explicit
    // "application/x-www-form-urlencoded" header overrode it and contradicted the JSON body Slack
    // was actually being sent.
    await ky.post(slackWebhookUrl, {
      json: {
        text:
          `New Inquiry from BadgeReg!\n*Name:* ${user_name}\n*Email:* ${user_email}` +
          (unit ? `\n*Unit:* ${unit}` : "") +
          `\n*Message:* ${message}`,
      },
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const errors = {};
    for (const [name, field] of Object.entries(REQUIRED_FIELDS)) {
      const message = field.validate(contactDetails[name]);
      if (message) {
        errors[name] = message;
      }
    }
    setFieldErrors(errors);

    const firstInvalid = Object.keys(REQUIRED_FIELDS).find(
      (name) => errors[name],
    );
    if (firstInvalid) {
      // Focus moves to the problem rather than leaving the reader to hunt for it, which for a screen
      // reader user is the difference between hearing the message and never knowing it exists.
      document.getElementById(REQUIRED_FIELDS[firstInvalid].id)?.focus();
      return;
    }

    setIsLoading(true);

    const fields = {
      name: contactDetails.user_name,
      email: contactDetails.user_email,
      unitType: contactDetails.unit_type,
      unitNumber: contactDetails.unit_number,
      expectedEventDate: contactDetails.expected_event_date,
      message: contactDetails.message,
      website: contactDetails.website,
    };

    let recordedByApi = false;
    try {
      recordedByApi = await submitLead(fields);
    } catch (error) {
      console.error("Lead submission to the API failed", error);
    }

    let delivered = recordedByApi;
    if (!delivered) {
      try {
        await sendToSlackWebhook();
        delivered = true;
      } catch (error) {
        console.error("Slack webhook fallback also failed", error);
      }
    }

    if (delivered) {
      // Only when the API did not take the lead itself. Same funnel pipeline as the demo, so a lead
      // that started as a site visit and one that started in the sandbox show up side by side rather
      // than in two separate reports — but the API records this event on a successful /demo/lead, so
      // firing it here as well counted every submission twice. The webhook fallback never reaches the
      // API, so that path still has to report itself or the lead goes missing from the funnel.
      if (!recordedByApi) {
        trackMarketingEvent(MARKETING_EVENTS.CONTACT_SUBMITTED);
      }
      setContactDetails({
        user_name: "",
        user_email: "",
        unit_type: "",
        unit_number: "",
        expected_event_date: "",
        message: "",
        website: "",
      });
      setSnackbar({
        open: true,
        message: "Thanks: we have your message and will be in touch shortly.",
        severity: "success",
      });
    } else {
      setSnackbar({
        open: true,
        message:
          "We could not send that. Please email support@badgereg.com and we will pick it up.",
        severity: "error",
      });
    }
    setIsLoading(false);
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  return (
    <Box
      p={4}
      style={{
        backgroundColor: theme.palette.background.default,
        flexGrow: 1,
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        minHeight: "200px",
      }}
    >
      <Container>
        {/* Stacks below 600px: side by side, the badge left "Contact Us" a column barely one word
            wide on a phone. */}
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          alignItems={{ xs: "flex-start", sm: "center" }}
        >
          <Tooltip title="Communications" arrow placement="top">
            <span>
              <img
                src="badges/Communications.svg"
                alt="Communications"
                height={56}
                width={56}
              />
            </span>
          </Tooltip>
          <Typography
            variant="h4"
            component="h1"
            gutterBottom
            sx={{
              color: theme.palette.text.primary,
              fontWeight: "bold",
              pb: { xs: 1, sm: 3 },
              pt: 1,
            }}
          >
            Contact Us
          </Typography>
        </Stack>
        <Typography
          variant="subtitle1"
          gutterBottom
          sx={{ color: theme.palette.text.secondary, pb: 0 }}
        >
          Reach out to us to get started or if you have any questions!
        </Typography>
        <Typography
          variant="subtitle1"
          gutterBottom
          sx={{ color: theme.palette.text.secondary, pb: 2 }}
        >
          Feel free to send up any feature requests or feedback as well :)
        </Typography>
        {/* `noValidate` hands validation to the handlers above rather than to the browser, whose
            bubble cannot be styled, vanishes on the next keystroke, and is not dependably announced.
            The `required` attributes stay: they are what expose the fields as required to assistive
            technology. */}
        <form onSubmit={handleSubmit} noValidate>
          <TextField
            label="Name"
            name="user_name"
            variant="outlined"
            fullWidth
            margin="normal"
            required
            autoComplete="name"
            onChange={handleChange}
            value={contactDetails.user_name}
            {...validationProps("user_name")}
          />
          <TextField
            label="Email"
            name="user_email"
            type="email"
            variant="outlined"
            fullWidth
            margin="normal"
            required
            autoComplete="email"
            onChange={handleChange}
            value={contactDetails.user_email}
            {...validationProps("user_email")}
          />
          {/* Optional, and grouped on one row from tablet up. These three answer the questions we
              would otherwise have to email back and forth to establish, but requiring them would cost
              more leads than the qualification is worth.
              The fieldset is what carries "optional" to somebody who cannot see that these three sit
              apart from the rest: without it the word appears nowhere in what a screen reader
              announces for any of them. */}
          <Box
            component="fieldset"
            sx={{ border: 0, p: 0, m: 0, minInlineSize: 0 }}
          >
            <Typography
              component="legend"
              variant="subtitle2"
              sx={{ color: theme.palette.text.secondary, p: 0, mt: 1 }}
            >
              About your event (optional)
            </Typography>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <TextField
                label="Unit type"
                name="unit_type"
                placeholder="Troop, Crew, Pack"
                variant="outlined"
                fullWidth
                margin="normal"
                // None of these three is one of the input purposes the autofill spec defines, and
                // guessing at the nearest token would have a browser offer a company name for a unit
                // number. Saying so explicitly stops it guessing for us.
                autoComplete="off"
                onChange={handleChange}
                value={contactDetails.unit_type}
              />
              <TextField
                label="Unit number"
                name="unit_number"
                placeholder="324"
                variant="outlined"
                fullWidth
                margin="normal"
                autoComplete="off"
                onChange={handleChange}
                value={contactDetails.unit_number}
              />
              <TextField
                label="Approximate event date"
                name="expected_event_date"
                placeholder="March 2027"
                variant="outlined"
                fullWidth
                margin="normal"
                autoComplete="off"
                onChange={handleChange}
                value={contactDetails.expected_event_date}
              />
            </Stack>
          </Box>
          {/* Hidden from people and from assistive technology, so only a bot fills it in. Positioned
              off-screen rather than display:none because some bots skip undisplayed inputs.
              `tabIndex` has to go through `inputProps`: on the TextField itself it lands on the
              wrapper div, which was never focusable anyway, leaving the input still in the tab order
              inside an aria-hidden container. A keyboard user tabbing from the message field landed
              on an invisible box they could type into and could not see. */}
          <TextField
            label="Website"
            name="website"
            value={contactDetails.website}
            onChange={handleChange}
            aria-hidden="true"
            autoComplete="off"
            inputProps={{ tabIndex: -1 }}
            sx={{
              position: "absolute",
              left: "-9999px",
              width: 1,
              height: 1,
              overflow: "hidden",
            }}
          />
          <TextField
            label="Message"
            name="message"
            variant="outlined"
            fullWidth
            margin="normal"
            required
            multiline
            rows={4}
            onChange={handleChange}
            value={contactDetails.message}
            {...validationProps("message")}
          />
          {/* The spinner used to be the button's entire contents while sending, which left it with
              no accessible name at the one moment somebody most wants to know what it is doing. The
              label stays and the spinner sits beside it, hidden from assistive technology because
              `aria-busy` already carries that meaning. */}
          <Button
            type="submit"
            variant="contained"
            color="primary"
            sx={{ mt: 3 }}
            disabled={isLoading}
            aria-busy={isLoading}
            startIcon={
              isLoading ? (
                <CircularProgress
                  size={18}
                  color="inherit"
                  aria-hidden="true"
                />
              ) : null
            }
          >
            {isLoading ? "Sending" : "Send"}
          </Button>
        </form>
      </Container>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <MuiAlert
          elevation={6}
          variant="filled"
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
        >
          {snackbar.message}
        </MuiAlert>
      </Snackbar>
    </Box>
  );
};

export default ContactForm;
